import { create } from 'zustand';
import { AuthenticatedUser, LoginResponse } from '../types/auth';
import { secureStorage } from '../utils/secureStorage';

const SESSION_STORAGE_KEY = 'inspecta.tech.session';

interface TechnicianSessionState {
  user: AuthenticatedUser | null;
  token: string | null;
  isLoading: boolean;
  authMessage: string | null;
  initializeSession: () => Promise<void>;
  setSession: (session: LoginResponse) => Promise<void>;
  clearSession: (message?: string) => Promise<void>;
  setLoading: (isLoading: boolean) => void;
  setAuthMessage: (message: string | null) => void;
}

export const useTechnicianSessionStore = create<TechnicianSessionState>()((set) => ({
  user: null,
  token: null,
  isLoading: true,
  authMessage: null,
  initializeSession: async () => {
    set({ isLoading: true });
    try {
      const storedSession = await secureStorage.getItem<LoginResponse>(SESSION_STORAGE_KEY);
      if (storedSession?.token && storedSession?.user) {
        set({
          user: storedSession.user,
          token: storedSession.token,
          isLoading: false,
          authMessage: null
        });
      } else {
        set({
          user: null,
          token: null,
          isLoading: false,
          authMessage: null
        });
      }
    } catch (error) {
      console.warn('Konnte gespeicherte Sitzung nicht laden.', error);
      set({
        user: null,
        token: null,
        isLoading: false,
        authMessage: null
      });
    }
  },
  setSession: async (session) => {
    set({
      user: session.user,
      token: session.token,
      isLoading: false,
      authMessage: null
    });
    try {
      await secureStorage.setItem(SESSION_STORAGE_KEY, session);
    } catch (error) {
      console.warn('Konnte Sitzung nicht speichern.', error);
    }
  },
  clearSession: async (message) => {
    try {
      await secureStorage.removeItem(SESSION_STORAGE_KEY);
    } catch (error) {
      console.warn('Konnte Sitzung nicht entfernen.', error);
    } finally {
      set({
        user: null,
        token: null,
        isLoading: false,
        authMessage: message ?? null
      });

      try {
        const module = await import('./inspectionOrdersStore');
        module.useInspectionOrdersStore.getState().clearTodaysOrders();
      } catch (error) {
        console.warn('Konnte Tagesaufträge nicht zurücksetzen.', error);
      }
    }
  },
  setLoading: (isLoading: boolean) => set({ isLoading }),
  setAuthMessage: (message: string | null) => set({ authMessage: message })
}));
